import { useEffect, useState } from 'react';

type MessageFile = {
	default: { [key: string]: string };
};

export function useFetchMessagesByLocale(locale: string): {
	isLoading: boolean;
	messages: Record<string, string>;
} {
	const [messages, setMessages] = useState<Record<string, string>>({});
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let isMounted = true;

		async function startToFetchMessagesByLocale() {
			// Intl.Locale doesn't like underscores
			const finalLocale = new Intl.Locale(locale.replace('_', '-')).toString();

			try {
				setIsLoading(true);
				const module = await fetchLanguageFile(finalLocale);
				const newMessages = module.default || module;
				if (isMounted) {
					setMessages(newMessages);
				}
			} catch (
				// eslint-disable-next-line no-unused-vars
				e
			) {
				// fail to download message and do nothing because UI will use default language (English) to render
			} finally {
				if (isMounted) {
					setIsLoading(false);
				}
			}
		}

		startToFetchMessagesByLocale();

		return () => {
			isMounted = false;
		};
	}, [locale]);

	return { messages, isLoading };
}

export function fetchLanguageFile(locale: string): Promise<MessageFile> {
	switch (locale) {
		case 'cs-CZ':
		case 'cs':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-cs" */ '../../../i18n/cs'
			);
		case 'da-DK':
		case 'da':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-da" */ '../../../i18n/da'
			);
		case 'de-DE':
		case 'de':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-de" */ '../../../i18n/de'
			);
		case 'en-GB':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-en-GB" */ '../../../i18n/en_GB'
			);
		case 'en-AU':
		case 'en-US':
		case 'en':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-en" */ '../../../i18n/en'
			);
		case 'en-ZZ':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-en-ZZ" */ '../../../i18n/en_ZZ'
			);
		case 'es-ES':
		case 'es':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-es" */ '../../../i18n/es'
			);
		case 'fi-FI':
		case 'fi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-fi" */ '../../../i18n/fi'
			);
		case 'fr-FR':
		case 'fr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-fr" */ '../../../i18n/fr'
			);
		case 'hu-HU':
		case 'hu':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-hu" */ '../../../i18n/hu'
			);
		case 'it-IT':
		case 'it':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-it" */ '../../../i18n/it'
			);
		case 'ja-JP':
		case 'ja':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-ja" */ '../../../i18n/ja'
			);
		case 'ko-KR':
		case 'ko':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-ko" */ '../../../i18n/ko'
			);
		case 'nb-NB':
		case 'nb':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-nb" */ '../../../i18n/nb'
			);
		case 'nl-NL':
		case 'nl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-nl" */ '../../../i18n/nl'
			);
		case 'pl-PL':
		case 'pl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-pl" */ '../../../i18n/pl'
			);
		case 'pt-BR':
		case 'pt':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-pt-BR" */ '../../../i18n/pt_BR'
			);
		case 'ru-RU':
		case 'ru':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-ru" */ '../../../i18n/ru'
			);
		case 'sv-SE':
		case 'sv':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-sv" */ '../../../i18n/sv'
			);
		case 'tr-TR':
		case 'tr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-tr" */ '../../../i18n/tr'
			);
		case 'th-TH':
		case 'th':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-th" */ '../../../i18n/th'
			);
		case 'uk-UK':
		case 'uk':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-uk" */ '../../../i18n/uk'
			);
		case 'vi-VI':
		case 'vi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-vi" */ '../../../i18n/vi'
			);
		case 'zh-TW':
		case 'zh-HK':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-zh-TW" */ '../../../i18n/zh_TW'
			);
		case 'zh-ZH':
		case 'zh-CN':
		case 'zh':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-zh-ZH" */ '../../../i18n/zh'
			);
		default:
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/cross-join-nudges-i18n-en" */ '../../../i18n/en'
			);
	}
}

/**
 * Fetches messages from @atlassian/global-shortcuts package for a given locale
 */
export function fetchGlobalShortcutsLanguageFile(locale: string): Promise<MessageFile> {
	// Normalize locale to match global-shortcuts format
	const normalizedLocale = locale.replace('_', '-');
	const languageLocale = normalizedLocale.split(/[-_]/)[0] || 'en';

	switch (normalizedLocale) {
		case 'cs-CZ':
		case 'cs':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-cs" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/cs'
			);
		case 'da-DK':
		case 'da':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-da" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/da'
			);
		case 'de-DE':
		case 'de':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-de" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/de'
			);
		case 'en-GB':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-en-GB" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/en_GB'
			);
		case 'en-AU':
		case 'en-US':
		case 'en':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-en" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/en'
			);
		case 'en-ZZ':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-en-ZZ" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/en_ZZ'
			);
		case 'es-ES':
		case 'es':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-es" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/es'
			);
		case 'fi-FI':
		case 'fi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-fi" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/fi'
			);
		case 'fr-FR':
		case 'fr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-fr" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/fr'
			);
		case 'hu-HU':
		case 'hu':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-hu" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/hu'
			);
		case 'it-IT':
		case 'it':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-it" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/it'
			);
		case 'ja-JP':
		case 'ja':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-ja" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/ja'
			);
		case 'ko-KR':
		case 'ko':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-ko" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/ko'
			);
		case 'nb-NB':
		case 'nb':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-nb" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/nb'
			);
		case 'nl-NL':
		case 'nl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-nl" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/nl'
			);
		case 'pl-PL':
		case 'pl':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-pl" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/pl'
			);
		case 'pt-BR':
		case 'pt':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-pt-BR" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/pt_BR'
			);
		case 'ru-RU':
		case 'ru':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-ru" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/ru'
			);
		case 'sv-SE':
		case 'sv':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-sv" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/sv'
			);
		case 'tr-TR':
		case 'tr':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-tr" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/tr'
			);
		case 'th-TH':
		case 'th':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-th" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/th'
			);
		case 'uk-UK':
		case 'uk':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-uk" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/uk'
			);
		case 'vi-VI':
		case 'vi':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-vi" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/vi'
			);
		case 'zh-TW':
		case 'zh-HK':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-zh-TW" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/zh_TW'
			);
		case 'zh-ZH':
		case 'zh-CN':
		case 'zh':
			return import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-zh" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/zh'
			);
		default:
			// Fallback to language code if specific locale not found
			if (languageLocale === 'en') {
				return import(
					/* webpackChunkName: "@atlaskit-internal_@atlassian/afm-i18n-platform-navigation-global-shortcuts-i18n-en" */ '@atlassian/afm-i18n-platform-navigation-global-shortcuts/i18n/en'
				);
			}
	}

	return Promise.resolve({ default: {} });
}
